import type {
  ColumnOrderState,
  ColumnPinningState,
  ColumnVisibilityState,
  OnChangeFn,
  TableState,
  StockFeatures,
} from '@tanstack/react-table'
import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'
import { SELECTION_COLUMN_ID } from './Table'

/** The slice of TanStack table state this primitive owns. */
export type TableViewColumnState = Pick<
  TableState<StockFeatures>,
  'columnOrder' | 'columnPinning' | 'columnVisibility'
>

export interface TableViewStateColumn {
  enableHiding?: boolean
  id: string
  meta?: {
    defaultHidden?: boolean
    defaultPin?: 'start' | 'end'
  }
}

export function getDefaultColumnVisibility(
  columns: ReadonlyArray<TableViewStateColumn>,
): ColumnVisibilityState {
  return Object.fromEntries(
    columns
      .filter((column) => {
        return column.meta?.defaultHidden
      })
      .map((column) => {
        return [column.id, false]
      }),
  )
}

export function getDefaultColumnPinning(
  columns: ReadonlyArray<TableViewStateColumn>,
): ColumnPinningState {
  const hasSelectionColumn = columns.some((column) => {
    return column.id === SELECTION_COLUMN_ID
  })
  const start = columns
    .filter((column) => {
      return (
        column.id !== SELECTION_COLUMN_ID && column.meta?.defaultPin === 'start'
      )
    })
    .map((column) => {
      return column.id
    })
  const end = columns
    .filter((column) => {
      return column.meta?.defaultPin === 'end'
    })
    .map((column) => {
      return column.id
    })
  return {
    end,
    start: hasSelectionColumn ? [SELECTION_COLUMN_ID, ...start] : start,
  }
}

export function withSelectionColumnPinnedStart(
  pinning: TableViewColumnState['columnPinning'],
): ColumnPinningState {
  const start = (pinning?.start ?? []).filter((columnId) => {
    return columnId !== SELECTION_COLUMN_ID
  })
  const end = (pinning?.end ?? []).filter((columnId) => {
    return columnId !== SELECTION_COLUMN_ID
  })
  return {
    end,
    start: [SELECTION_COLUMN_ID, ...start],
  }
}

const PINNED_START_COLUMN_MARKER = '<'
const PINNED_END_COLUMN_MARKER = '>'

type SerializedColumnPin = 'start' | 'end' | false

function parseSerializedColumnToken(token: string): {
  columnId: string
  pin: SerializedColumnPin
} {
  if (token.endsWith(PINNED_START_COLUMN_MARKER)) {
    return { columnId: token.slice(0, -1), pin: 'start' }
  }

  if (token.endsWith(PINNED_END_COLUMN_MARKER)) {
    return { columnId: token.slice(0, -1), pin: 'end' }
  }

  return { columnId: token, pin: false }
}

function serializeColumnToken(columnId: string, pin: SerializedColumnPin) {
  if (pin === 'start') {
    return `${columnId}${PINNED_START_COLUMN_MARKER}`
  }

  if (pin === 'end') {
    return `${columnId}${PINNED_END_COLUMN_MARKER}`
  }

  return columnId
}

function hiddenColumnIdSet(columnVisibility: ColumnVisibilityState) {
  return new Set(
    Object.entries(columnVisibility)
      .filter(([, isVisible]) => {
        return isVisible === false
      })
      .map(([columnId]) => {
        return columnId
      }),
  )
}

function completeVisibleColumnOrder({
  columnOrder,
  columnPinning,
  columnVisibility,
  orderableColumnIds,
}: {
  columnOrder: Array<string>
  columnPinning: ColumnPinningState
  columnVisibility: ColumnVisibilityState
  orderableColumnIds: Array<string>
}) {
  const knownColumnIds = new Set(orderableColumnIds)
  const startPinnedColumnIds = (columnPinning.start ?? []).filter(
    (columnId) => {
      return (
        columnId !== SELECTION_COLUMN_ID &&
        knownColumnIds.has(columnId) &&
        columnVisibility[columnId] !== false
      )
    },
  )
  const endPinnedColumnIds = (columnPinning.end ?? []).filter((columnId) => {
    return knownColumnIds.has(columnId) && columnVisibility[columnId] !== false
  })
  const startPinnedColumnIdSet = new Set(startPinnedColumnIds)
  const endPinnedColumnIdSet = new Set(endPinnedColumnIds)
  const sourceOrder =
    columnOrder.length === 0 ? orderableColumnIds : columnOrder
  const unpinnedVisibleFromSource = sourceOrder.filter((columnId) => {
    return (
      knownColumnIds.has(columnId) &&
      columnId !== SELECTION_COLUMN_ID &&
      startPinnedColumnIdSet.has(columnId) === false &&
      endPinnedColumnIdSet.has(columnId) === false &&
      columnVisibility[columnId] !== false
    )
  })
  const placedColumnIdSet = new Set([
    ...startPinnedColumnIds,
    ...unpinnedVisibleFromSource,
    ...endPinnedColumnIds,
  ])
  const missingUnpinnedVisibleColumnIds = orderableColumnIds.filter(
    (columnId) => {
      return (
        placedColumnIdSet.has(columnId) === false &&
        columnVisibility[columnId] !== false
      )
    },
  )
  return [
    ...startPinnedColumnIds,
    ...unpinnedVisibleFromSource,
    ...missingUnpinnedVisibleColumnIds,
    ...endPinnedColumnIds,
  ]
}

function isTableViewColumnStateEqual(
  a: TableViewColumnState,
  b: TableViewColumnState,
) {
  if (JSON.stringify(a.columnOrder) !== JSON.stringify(b.columnOrder)) {
    return false
  }

  const aPinnedStart = a.columnPinning?.start ?? []
  const bPinnedStart = b.columnPinning?.start ?? []
  const aPinnedEnd = a.columnPinning?.end ?? []
  const bPinnedEnd = b.columnPinning?.end ?? []
  const isStartPinningEqual =
    JSON.stringify(aPinnedStart) === JSON.stringify(bPinnedStart)
  const isEndPinningEqual =
    JSON.stringify(aPinnedEnd) === JSON.stringify(bPinnedEnd)
  const isPinningEqual = isStartPinningEqual && isEndPinningEqual
  if (isPinningEqual === false) {
    return false
  }

  const aHidden = hiddenColumnIdSet(a.columnVisibility)
  const bHidden = hiddenColumnIdSet(b.columnVisibility)
  if (aHidden.size !== bHidden.size) {
    return false
  }

  return [...aHidden].every((columnId) => {
    return bHidden.has(columnId)
  })
}

/**
 * The `columns` param holds one token per visible column, in display order,
 * suffixed `<` or `>` when pinned. The parser is column-agnostic so a route can
 * list it in its own query param map and carry it on links.
 */
export const TableColumnViewQueryParams = {
  columns: parseAsArrayOf(parseAsString),
}

export interface UseTableColumnViewQueryStateParams {
  columns: ReadonlyArray<TableViewStateColumn>
}

export interface TableColumnViewQueryState {
  isModified: () => boolean
  onColumnOrderChange: OnChangeFn<ColumnOrderState>
  onColumnPinningChange: OnChangeFn<ColumnPinningState>
  onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState>
  reset: () => void
  setState: (
    updater: (prev: TableViewColumnState) => TableViewColumnState,
  ) => void
  state: TableViewColumnState
}

export function useTableColumnViewQueryState({
  columns,
}: UseTableColumnViewQueryStateParams): TableColumnViewQueryState {
  const orderableColumnIds = columns
    .map((column) => {
      return column.id
    })
    .filter((columnId) => {
      return columnId !== SELECTION_COLUMN_ID
    })

  const defaultColumnPinning = getDefaultColumnPinning(columns)

  const knownColumnIds = new Set(orderableColumnIds)
  const hideableColumnIds = new Set(
    columns
      .filter((column) => {
        return (
          column.enableHiding !== false && column.id !== SELECTION_COLUMN_ID
        )
      })
      .map((column) => {
        return column.id
      }),
  )

  const defaultState: TableViewColumnState = {
    columnOrder: [],
    columnPinning: defaultColumnPinning,
    columnVisibility: getDefaultColumnVisibility(columns),
  }

  const parseColumnTokens = (
    columnTokens: Array<string>,
  ): TableViewColumnState => {
    const tokens = columnTokens
      .filter((token) => {
        return token.length > 0
      })
      .map(parseSerializedColumnToken)
      .filter((token) => {
        return knownColumnIds.has(token.columnId)
      })

    const presentColumnIds = tokens.map((token) => {
      return token.columnId
    })
    const presentColumnIdSet = new Set(presentColumnIds)

    const columnVisibility = orderableColumnIds.reduce<ColumnVisibilityState>(
      (acc, columnId) => {
        if (
          hideableColumnIds.has(columnId) &&
          presentColumnIdSet.has(columnId) === false
        ) {
          acc[columnId] = false
        }

        return acc
      },
      {},
    )

    const omittedVisibleColumnIdSet = new Set(
      orderableColumnIds.filter((columnId) => {
        return (
          presentColumnIdSet.has(columnId) === false &&
          columnVisibility[columnId] !== false
        )
      }),
    )
    const startPinnedFromTokens = tokens
      .filter((token) => {
        return token.pin === 'start'
      })
      .map((token) => {
        return token.columnId
      })
    const endPinnedFromTokens = tokens
      .filter((token) => {
        return token.pin === 'end'
      })
      .map((token) => {
        return token.columnId
      })
    const startPinnedFromTokensSet = new Set(startPinnedFromTokens)
    const endPinnedFromTokensSet = new Set(endPinnedFromTokens)
    const startPinnedColumnIds = [
      ...startPinnedFromTokens,
      ...(defaultColumnPinning.start ?? []).filter((columnId) => {
        return (
          columnId !== SELECTION_COLUMN_ID &&
          omittedVisibleColumnIdSet.has(columnId) &&
          startPinnedFromTokensSet.has(columnId) === false
        )
      }),
    ]
    const endPinnedColumnIds = [
      ...endPinnedFromTokens,
      ...(defaultColumnPinning.end ?? []).filter((columnId) => {
        return (
          omittedVisibleColumnIdSet.has(columnId) &&
          endPinnedFromTokensSet.has(columnId) === false
        )
      }),
    ]
    const completedColumnOrder = completeVisibleColumnOrder({
      columnOrder: presentColumnIds,
      columnPinning: {
        end: endPinnedColumnIds,
        start: startPinnedColumnIds,
      },
      columnVisibility,
      orderableColumnIds,
    })

    return {
      columnOrder:
        JSON.stringify(completedColumnOrder) ===
        JSON.stringify(orderableColumnIds)
          ? []
          : completedColumnOrder,
      columnPinning: {
        end: endPinnedColumnIds,
        start: [SELECTION_COLUMN_ID, ...startPinnedColumnIds],
      },
      columnVisibility,
    }
  }

  const serializeColumnState = (value: TableViewColumnState) => {
    const startPinnedColumnIds = new Set(value.columnPinning?.start ?? [])
    const endPinnedColumnIds = new Set(value.columnPinning?.end ?? [])
    return completeVisibleColumnOrder({
      columnOrder: value.columnOrder,
      columnPinning: value.columnPinning ?? { end: [], start: [] },
      columnVisibility: value.columnVisibility,
      orderableColumnIds,
    }).map((columnId) => {
      const pin: SerializedColumnPin = startPinnedColumnIds.has(columnId)
        ? 'start'
        : endPinnedColumnIds.has(columnId)
          ? 'end'
          : false
      return serializeColumnToken(columnId, pin)
    })
  }

  const parseColumnTokensOrDefault = (columnTokens: Array<string> | null) => {
    return columnTokens ? parseColumnTokens(columnTokens) : defaultState
  }

  const [queryState, setQueryState] = useQueryStates(TableColumnViewQueryParams)
  const state = parseColumnTokensOrDefault(queryState.columns)

  const setState = (
    updater: (prev: TableViewColumnState) => TableViewColumnState,
  ) => {
    void setQueryState((prev) => {
      const previousState = parseColumnTokensOrDefault(prev.columns)
      const nextState = updater(previousState)
      return {
        columns: isTableViewColumnStateEqual(nextState, defaultState)
          ? null
          : serializeColumnState(nextState),
      }
    })
  }

  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = (next) => {
    setState((prev) => {
      const nextOrder =
        typeof next === 'function' ? next(prev.columnOrder) : next
      return {
        ...prev,
        columnOrder: nextOrder,
      }
    })
  }

  const onColumnPinningChange: OnChangeFn<ColumnPinningState> = (next) => {
    setState((prev) => {
      const previousPinning = prev.columnPinning ?? { end: [], start: [] }
      const nextPinning =
        typeof next === 'function' ? next(previousPinning) : next
      return {
        ...prev,
        columnPinning: withSelectionColumnPinnedStart(nextPinning),
      }
    })
  }

  const onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = (
    next,
  ) => {
    setState((prev) => {
      const nextVisibility =
        typeof next === 'function' ? next(prev.columnVisibility) : next
      const hiddenColumnIds = hiddenColumnIdSet(nextVisibility)
      return {
        ...prev,
        columnOrder: prev.columnOrder.filter((columnId) => {
          return hiddenColumnIds.has(columnId) === false
        }),
        columnVisibility: nextVisibility,
      }
    })
  }

  const reset = () => {
    void setQueryState({ columns: null })
  }

  const isModified = () => {
    return isTableViewColumnStateEqual(state, defaultState) === false
  }

  return {
    isModified,
    onColumnOrderChange,
    onColumnPinningChange,
    onColumnVisibilityChange,
    reset,
    setState,
    state,
  }
}
